import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowUpRight, X } from "lucide-react";

export function Button({
  children,
  variant = "secondary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  return (
    <button
      type="button"
      className={`btn btn--${variant} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
export function IconButton({
  label,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`icon-button ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}
export function Panel({
  title,
  description,
  action,
  children,
  className = "",
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || action) && (
        <header className="panel-header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action}
        </header>
      )}
      <div className="panel-body">{children}</div>
    </section>
  );
}
export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "green" | "blue" | "amber" | "red" | "neutral" | "purple";
}) {
  return (
    <span className={`badge badge--${tone}`}>
      <span className="badge-dot" />
      {children}
    </span>
  );
}
export function Avatar({
  name,
  color,
  size = "md",
}: {
  name: string;
  color?: string;
  size?: "sm" | "md" | "lg";
}) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0] ?? "")
    .join("");
  return (
    <span
      className={`avatar avatar--${size}`}
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  const id = useId();
  const child = Children.only(children);
  const content = isValidElement(child)
    ? cloneElement(
        child as ReactElement<{ id?: string; "aria-describedby"?: string }>,
        {
          id: (child.props as { id?: string }).id || id,
          ...(hint
            ? {
                "aria-describedby": [
                  (child.props as { "aria-describedby"?: string })[
                    "aria-describedby"
                  ],
                  `${id}-hint`,
                ]
                  .filter(Boolean)
                  .join(" "),
              }
            : {}),
        },
      )
    : child;
  return (
    <div className="field">
      <label
        htmlFor={
          isValidElement(child) ? (child.props as { id?: string }).id || id : id
        }
      >
        {label}
      </label>
      {content}
      {hint && <small id={`${id}-hint`}>{hint}</small>}
    </div>
  );
}
export function Modal({
  open,
  onClose,
  title,
  description,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  const descriptionId = useId();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content
          className={`modal ${wide ? "modal--wide" : ""}`}
          aria-describedby={description ? descriptionId : undefined}
        >
          <div className="modal-header">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              {description && (
                <Dialog.Description id={descriptionId}>
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton label="Close dialog">
                <X size={18} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="modal-body">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-icon">{icon}</div>}
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function Metric({
  label,
  value,
  change,
  note,
  icon,
  featured,
}: {
  label: string;
  value: string;
  change?: string;
  note?: string;
  icon?: ReactNode;
  featured?: boolean;
}) {
  return (
    <article
      className={`metric-card ${featured ? "metric-card--featured" : ""}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        <span className="metric-glyph">
          {icon || <ArrowUpRight size={18} />}
        </span>
      </div>
      <strong>{value}</strong>
      <div className="metric-bottom">
        {change && <span className="metric-change">{change}</span>}
        <span>{note}</span>
      </div>
    </article>
  );
}
export function Progress({
  value,
  label,
  color,
}: {
  value: number;
  label: string;
  color?: string;
}) {
  const normalized = Math.max(0, Math.min(100, value));
  return (
    <div
      className="progress-track"
      role="progressbar"
      aria-label={label}
      aria-valuenow={normalized}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span
        style={{
          width: `${normalized}%`,
          ...(color ? { background: color } : {}),
        }}
      />
    </div>
  );
}
export function ConfirmDialog({
  open,
  title = "Delete this record?",
  description = "This record will be removed from this workspace.",
  onConfirm,
  onClose,
}: {
  open: boolean;
  title?: string;
  description?: string;
  onConfirm: () => boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
    >
      <div className="form-actions">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="danger"
          onClick={() => {
            if (onConfirm()) onClose();
          }}
        >
          Delete record
        </Button>
      </div>
    </Modal>
  );
}
